import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Eye, FileText, FlaskConical, Save, Send, X, BookmarkPlus } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import RichTextEditor from "@/components/RichTextEditor";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import AdminCard from "../AdminCard";
import GroupPicker from "./GroupPicker";
import AttachmentsField from "./AttachmentsField";
import TemplatePickerModal from "./TemplatePickerModal";
import ScheduleModal from "./ScheduleModal";
import TemplateEditModal from "./TemplateEditModal";
import { warsawTime } from "./templateUtils";

const EMPTY = { id: null, status: "draft", subject: "", html: "", group_ids: [], attachments: [], event_date: "" };

const PreviewModal = ({ html, onClose }) => (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="mailing-preview-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b">
                <h3 className="font-serif text-xl text-[#092E22]">Podgląd wiadomości</h3>
                <button onClick={onClose} data-testid="mailing-preview-close" className="p-1.5 rounded-lg hover:bg-gray-100"><X size={18} /></button>
            </div>
            <iframe title="Podgląd" srcDoc={html} className="flex-1 w-full bg-[#F4F1EA]" sandbox="" />
        </div>
    </div>
);

export const CampaignComposer = ({ groups, draft, onSaved }) => {
    const [c, setC] = useState(EMPTY);
    const [busy, setBusy] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");
    const [editorKey, setEditorKey] = useState(0);
    const [modal, setModal] = useState("");

    useEffect(() => {
        if (draft) { setC({ ...EMPTY, ...draft }); setEditorKey((k) => k + 1); }
    }, [draft]);

    const body = () => ({ subject: c.subject, html: c.html, group_ids: c.group_ids, attachments: c.attachments, event_date: c.event_date || "" });
    const run = async (name, fn) => {
        setBusy(name);
        try { await fn(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(""); }
    };

    const persist = async () => {
        if (!c.subject.trim()) throw { response: { data: { detail: "Podaj temat wiadomości." } } };
        const { data } = c.id ? await api.put(`/mailing/campaigns/${c.id}`, body()) : await api.post("/mailing/campaigns", body());
        setC((prev) => ({ ...prev, id: data.id }));
        return data.id;
    };

    const reset = () => { setC(EMPTY); setEditorKey((k) => k + 1); };

    const applyTemplate = ({ subject, html }, date) => {
        if ((c.subject || c.html.replace(/<[^>]*>/g, "").trim()) && !window.confirm("Zastąpić obecny temat i treść szablonem?")) return;
        setC((prev) => ({ ...prev, subject, html, event_date: date }));
        setEditorKey((k) => k + 1);
        setModal("");
        toast.success("Wstawiono szablon – sprawdź treść przed wysyłką.");
    };

    const validate = () => {
        if (!c.group_ids.length) { toast.error("Wybierz przynajmniej jedną grupę odbiorców."); return false; }
        if (!c.html.replace(/<[^>]*>/g, "").trim()) { toast.error("Wpisz treść wiadomości."); return false; }
        return true;
    };

    const schedule = (scheduled_local) => run("schedule", async () => {
        const id = await persist();
        const { data } = await api.post(`/mailing/campaigns/${id}/schedule`, { scheduled_local });
        toast.success(`Zaplanowano wysyłkę na ${warsawTime(data.scheduled_at)}.`);
        setModal(""); reset(); onSaved?.();
    });
    const unschedule = () => run("unschedule", async () => {
        await api.post(`/mailing/campaigns/${c.id}/unschedule`);
        setC((prev) => ({ ...prev, status: "draft", scheduled_at: null }));
        toast.success("Anulowano planowanie – kampania wróciła do szkiców."); onSaved?.();
    });

    const saveDraft = () => run("save", async () => { await persist(); toast.success(c.status === "scheduled" ? "Zmiany zapisane – termin wysyłki bez zmian." : "Szkic zapisany."); onSaved?.(); });
    const preview = () => run("preview", async () => { const { data } = await api.post("/mailing/preview", body()); setPreviewHtml(data.html); });
    const test = () => run("test", async () => { const { data } = await api.post("/mailing/test", body()); toast.success(`Wysłano test na ${data.to}.`); });
    const send = () => run("send", async () => {
        if (!validate()) return;
        if (!window.confirm("Wysłać kampanię do wszystkich odbiorców z wybranych grup? Tej operacji nie można cofnąć.")) return;
        const id = await persist();
        const { data } = await api.post(`/mailing/campaigns/${id}/send`);
        toast.success(`Rozpoczęto wysyłkę do ${data.total} odbiorców. Postęp zobaczysz w historii.`);
        reset();
        onSaved?.();
    });

    return (
        <AdminCard>
            <div className="flex items-center justify-between gap-3 mb-5">
                <h3 className="font-serif text-xl text-[#092E22]">{!c.id ? "Nowa kampania" : ({ scheduled: "Zaplanowana kampania", failed: "Nieudana kampania" }[c.status] || "Edycja szkicu")}</h3>
                <div className="flex items-center gap-3">
                    <Button type="button" variant="outline" size="sm" onClick={() => setModal("templates")} className="rounded-full border-[#D4A537] text-[#92701f]" data-testid="mailing-templates-btn"><FileText size={13} className="mr-1.5" />Szablony</Button>
                    {c.id && <button onClick={reset} className="text-xs text-gray-500 hover:text-[#0D4F3C] underline" data-testid="mailing-new-campaign">+ Nowa kampania</button>}
                </div>
            </div>
            {c.status === "failed" && c.error && (
                <div className="mb-5 rounded-2xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800" data-testid="mailing-failed-banner">
                    {c.error} Popraw kampanię i zaplanuj lub wyślij ponownie.
                </div>
            )}
            {c.status === "scheduled" && (
                <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-900" data-testid="mailing-scheduled-banner">
                    <CalendarClock size={16} /> Zaplanowano na <strong>{warsawTime(c.scheduled_at)}</strong>. Zmiany zapisuj przyciskiem „Zapisz”.
                    <button onClick={unschedule} disabled={!!busy} className="ml-auto text-xs underline" data-testid="mailing-unschedule-btn">Anuluj planowanie</button>
                </div>
            )}
            <div className="space-y-5" data-testid="mailing-composer">
                <div className="space-y-2">
                    <Label>Temat *</Label>
                    <Input value={c.subject} onChange={(e) => setC({ ...c, subject: e.target.value })} placeholder="np. Przegląd instalacji gazowej – 12 listopada" data-testid="mailing-subject" />
                </div>
                <div className="space-y-2">
                    <Label>Odbiorcy *</Label>
                    <GroupPicker groups={groups} value={c.group_ids} onChange={(group_ids) => setC({ ...c, group_ids })} />
                </div>
                <div className="space-y-2">
                    <Label>Treść *</Label>
                    <RichTextEditor key={editorKey} value={c.html} onChange={(html) => setC((prev) => ({ ...prev, html }))} placeholder="Treść wiadomości – formatowanie, listy, linki, obrazy..." testid="mailing-body" />
                </div>
                <div className="space-y-2">
                    <Label>Załączniki</Label>
                    <AttachmentsField value={c.attachments} onChange={(attachments) => setC({ ...c, attachments })} />
                </div>
                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100">
                    <Button type="button" variant="outline" onClick={preview} disabled={!!busy} className="rounded-full" data-testid="mailing-preview-btn"><Eye size={14} className="mr-2" />Podgląd</Button>
                    <Button type="button" variant="outline" onClick={test} disabled={!!busy} className="rounded-full" data-testid="mailing-test-btn"><FlaskConical size={14} className="mr-2" />{busy === "test" ? "Wysyłanie..." : "Wyślij test do mnie"}</Button>
                    <Button type="button" variant="outline" onClick={saveDraft} disabled={!!busy} className="rounded-full" data-testid="mailing-save-btn"><Save size={14} className="mr-2" />{c.status === "scheduled" ? "Zapisz" : "Zapisz szkic"}</Button>
                    <Button type="button" variant="outline" onClick={() => setModal("save-template")} disabled={!!busy || !c.subject} className="rounded-full" data-testid="mailing-save-template-btn"><BookmarkPlus size={14} className="mr-2" />Zapisz jako szablon</Button>
                    <Button type="button" variant="outline" onClick={() => validate() && setModal("schedule")} disabled={!!busy} className="rounded-full border-[#0D4F3C] text-[#0D4F3C] ml-auto" data-testid="mailing-schedule-btn"><CalendarClock size={14} className="mr-2" />{c.status === "scheduled" ? "Zmień termin" : "Zaplanuj"}</Button>
                    <Button type="button" onClick={send} disabled={!!busy} className="rounded-full bg-[#0D4F3C] hover:bg-[#092E22] text-white" data-testid="mailing-send-btn"><Send size={14} className="mr-2" />{busy === "send" ? "Uruchamianie..." : "Wyślij teraz"}</Button>
                </div>
            </div>
            {previewHtml && <PreviewModal html={previewHtml} onClose={() => setPreviewHtml("")} />}
            {modal === "templates" && <TemplatePickerModal onClose={() => setModal("")} onApply={applyTemplate} />}
            {modal === "schedule" && <ScheduleModal eventDate={c.event_date} current={c.status === "scheduled" ? c.scheduled_at : null} busy={busy === "schedule"} onClose={() => setModal("")} onConfirm={schedule} />}
            {modal === "save-template" && (
                <TemplateEditModal compact initial={{ name: c.subject, subject: c.subject, html: c.html }} onClose={() => setModal("")} onSaved={() => setModal("")} />
            )}
        </AdminCard>
    );
};

export default CampaignComposer;
